import Layout from "../../hocs/Layout";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { connect } from "react-redux";
import { signup } from "../../redux/actions/auth";
import { Circles } from "react-loader-spinner";
import { Button, Col, Form, Row, Container, Card } from "react-bootstrap";
import AutocompleteInput from "../../components/inputs/AutocompleteInput";
import { TextInput } from "../../components/inputs/Inputs";
import { getOfficesERP, getSupervisors, registerUserErp } from "../../services/usuarios";
import { signupSchema } from "../../utils/signupSchema";
import { toast } from "react-toastify";

const Signup = ({ signup }) => {
  const [usersOptions, setUsersOptions] = useState([{ value: "", label: "Sin supervisor" }]);
  const [officesOptions, setOfficesOptions] = useState([]);
  const [loadingOffices, setLoadingOffices] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const defaultValues = {
    email: "",
    password: "",
    re_password: "",
    first_name: "",
    last_name: "",
    document_id: "",
    phone: "",
    responsible: "",
    prescriptor: "",
    bank_account: "",
    coeficient_d: 7,
    erp_user_id: "",
    price_list_preffix: "",
    parent: "",
    delegacion: "",
    is_internal_agent: false,
    is_call_agent: false,
  };

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    trigger,
    watch,
  } = useForm({
    resolver: yupResolver(signupSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues
  });

  const isInternal = watch('is_internal_agent');
  const isCallAgent = watch('is_call_agent');
  const isBackofficeAgent = Boolean(isInternal);

  // Supervisores
  useEffect(() => {
    const fetchSupervisors = async () => {
      try {
        const supervisors = await getSupervisors();
        const options = supervisors.map(s => ({ value: s.id, label: s.name }));
        setUsersOptions([{ value: "", label: "Sin supervisor" }, ...options]);
      } catch (error) {
        console.error("Error fetching supervisors:", error);
      }
    };
    fetchSupervisors();
  }, []);

  // Oficinas / Delegaciones
  useEffect(() => {
    const fetchOffices = async () => {
      try {
        setLoadingOffices(true);
        const offices = await getOfficesERP(); // [{ id, name }, ...]
        setOfficesOptions(offices.map(o => ({ value: String(o.id), label: o.name })));
      } catch (error) {
        console.error("Error fetching offices:", error);
      } finally {
        setLoadingOffices(false);
      }
    };
    fetchOffices();
  }, []);

  const onSubmit = async (data) => {
    setLoading(true);
    let userErp = data.erp_user_id;      // podría venir vacío
    let userOVA = null;
    const createdErpHere = !isInternal && !isCallAgent && !userErp;

    try {
      const delegacionId = data.delegacion ? parseInt(data.delegacion, 10) : null;

      // Solo autogeneramos en ERP para agentes externos no CallAgent.
      if (createdErpHere) {
        userErp = await registerUserErp({
          user: {
            first_name: data.first_name,
            last_name: data.last_name,
            email: data.email,
          },
          datos: { delegacion: delegacionId },
        });
      }

      userOVA = await signup(
        data.email,
        data.password,
        data.re_password,
        data.first_name,
        data.last_name,
        data.document_id,
        data.phone,
        data.responsible || "",
        data.prescriptor || "",
        data.bank_account || "",
        data.coeficient_d,
        data.parent || "",
        data.price_list_preffix || "",
        userErp || null,
        delegacionId,
        data.is_internal_agent || false,
        data.is_call_agent || false
      );

      // ✅ Navega solo si todo OK
      if (userOVA?.ok) {
        navigate("/");
        return;
      }

    } catch (error) {
      console.error("Error en signup:", error);
      toast.error("Ocurrió un error inesperado durante el registro.", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: false
      });
    } finally {
      setLoading(false);
    }
  };



  return (
    <Layout>
      <div className="min-vh-100 d-flex align-items-center bg-light py-5">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} md={10} xs={12}>
              <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
                <Card.Header className="bg-white border-bottom-0 pt-4 pb-0 text-center">
                  <h2 className="text-primary fw-bold mb-0">Registro nuevos usuarios</h2>
                </Card.Header>
                <Card.Body className="p-4 p-md-5">
                  <Form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                    <Row className="mb-3">
                      <Col md={12}>
                        <Controller
                          name="is_internal_agent"
                          control={control}
                          render={({ field }) => (
                            <Form.Check
                              {...field}
                              type="switch"
                              id="custom-switch"
                              label="Es agente interno"
                              className="mb-3 fw-bold text-muted"
                              checked={!!field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                            />
                          )}
                        />
                        <Controller
                          name="is_call_agent"
                          control={control}
                          render={({ field }) => (
                            <Form.Check
                              {...field}
                              type="switch"
                              id="call-agent-switch"
                              label="Es agente call"
                              className="mb-3 fw-bold text-muted"
                              checked={!!field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                            />
                          )}
                        />
                      </Col>
                      {isBackofficeAgent && (
                        <Col md={12}>
                          <Controller
                            name="erp_user_id"
                            control={control}
                            render={({ field }) => (
                              <TextInput
                                field={field}
                                name="erp_user_id"
                                label="ID de usuario en ERP (Obligatorio solo para interno)"
                                autoComplete="off"
                                error={errors.erp_user_id?.message}
                              />
                            )}
                          />
                        </Col>
                      )}
                    </Row>

                    <Row className="g-3 mb-3">
                      <Col md={6}>
                        <Controller
                          name="first_name"
                          control={control}
                          render={({ field }) => (
                            <TextInput
                              field={field}
                              name="first_name"
                              label="Nombre / Razón Social"
                              autoComplete="off"
                              error={errors.first_name?.message}
                            />
                          )}
                        />
                      </Col>
                      <Col md={6}>
                        <Controller
                          name="last_name"
                          control={control}
                          render={({ field }) => (
                            <TextInput
                              field={field}
                              name="last_name"
                              label="Apellidos"
                              autoComplete="off"
                              error={errors.last_name?.message}
                            />
                          )}
                        />
                      </Col>
                    </Row>

                    <Row className="g-3 mb-3">
                      <Col md={6}>
                        <Controller
                          name="document_id"
                          control={control}
                          render={({ field }) => (
                            <TextInput
                              field={field}
                              name="document_id"
                              label="Documento (DNI/CIF)"
                              autoComplete="off"
                              error={errors.document_id?.message}
                            />
                          )}
                        />
                      </Col>
                      <Col md={6}>
                        <Controller
                          name="coeficient_d"
                          control={control}
                          render={({ field }) => (
                            <TextInput
                              field={field}
                              name="coeficient_d"
                              label="Coeficiente D"
                              autoComplete="off"
                              error={errors.coeficient_d?.message}
                            />
                          )}
                        />
                      </Col>
                    </Row>

                    <Row className="g-3 mb-3">
                      <Col md={6}>
                        <Controller
                          name="phone"
                          control={control}
                          render={({ field }) => (
                            <TextInput
                              field={field}
                              name="phone"
                              label="Teléfono"
                              autoComplete="off"
                              error={errors.phone?.message}
                            />
                          )}
                        />
                      </Col>
                      <Col md={6}>
                        <Controller
                          name="email"
                          control={control}
                          render={({ field }) => (
                            <TextInput
                              field={field}
                              name="email"
                              label="Correo electrónico"
                              type="email"
                              autoComplete="off"
                              error={errors.email?.message}
                            />
                          )}
                        />
                      </Col>
                    </Row>

                    <Row className="g-3 mb-3">
                      <Col md={6}>
                        <Controller
                          name="responsible"
                          control={control}
                          render={({ field }) => (
                            <TextInput
                              field={field}
                              name="responsible"
                              label="Gestor de cuenta"
                              autoComplete="off"
                            />
                          )}
                        />
                      </Col>
                      <Col md={6}>
                        <Controller
                          name="prescriptor"
                          control={control}
                          render={({ field }) => (
                            <TextInput
                              field={field}
                              name="prescriptor"
                              label="Prescriptor (opcional, números)"
                              autoComplete="off"
                              error={errors.prescriptor?.message}
                            />
                          )}
                        />
                      </Col>
                    </Row>

                    <Row className="g-3 mb-3">
                      <Col md={6}>
                        <Controller
                          name="bank_account"
                          control={control}
                          render={({ field }) => (
                            <TextInput
                              field={field}
                              name="bank_account"
                              label="IBAN"
                              autoComplete="off"
                              onChange={(e) => {
                                field.onChange(e);
                                trigger("bank_account");
                              }}
                              error={errors.bank_account?.message}
                            />
                          )}
                        />
                      </Col>
                      <Col md={6}>
                        <Controller
                          name="delegacion"
                          control={control}
                          render={({ field }) => {
                            const selected =
                              officesOptions.find(o => o.value === field.value) || null;
                            return (
                              <div className="form-floating">
                                <AutocompleteInput
                                  options={officesOptions}
                                  value={selected}
                                  onChange={(opt) => field.onChange(opt ? opt.value : "")}
                                  placeholder="Delegación"
                                />
                                <div className="text-muted small mt-1 ms-1">Delegación</div>
                              </div>
                            );
                          }}
                        />
                      </Col>
                    </Row>

                    <Row className="g-3 mb-3">
                      <Col md={6}>
                        <Controller
                          name="price_list_preffix"
                          control={control}
                          render={({ field }) => (
                            <TextInput
                              field={field}
                              name="price_list_preffix"
                              label="Prefijo Tarifas (opcional)"
                              autoComplete="off"
                            />
                          )}
                        />
                      </Col>
                      <Col md={6}>
                        <Controller
                          name="parent"
                          control={control}
                          render={({ field }) => {
                            const selected =
                              usersOptions.find(o => o.value === field.value) || null;
                            return (
                              <div className="form-floating">
                                <AutocompleteInput
                                  options={usersOptions}
                                  value={selected}
                                  onChange={(opt) => field.onChange(opt ? opt.value : "")}
                                  placeholder="Supervisor"
                                />
                                <div className="text-muted small mt-1 ms-1">Supervisor</div>
                              </div>
                            );
                          }}
                        />
                      </Col>
                    </Row>

                    <hr className="my-4 text-muted" />

                    <Row className="g-3 mb-4">
                      <Col md={6}>
                        <Controller
                          name="password"
                          control={control}
                          render={({ field }) => (
                            <TextInput
                              field={field}
                              name="password"
                              label="Contraseña"
                              type="password"
                              autoComplete="new-password"
                              onChange={(e) => {
                                field.onChange(e);
                                trigger("password");
                              }}
                              error={errors.password?.message}
                            />
                          )}
                        />
                      </Col>
                      <Col md={6}>
                        <Controller
                          name="re_password"
                          control={control}
                          render={({ field }) => (
                            <TextInput
                              field={field}
                              name="re_password"
                              label="Repetir Contraseña"
                              type="password"
                              autoComplete="new-password"
                              onChange={(e) => {
                                field.onChange(e);
                                trigger(["password", "re_password"]);
                              }}
                              error={errors.re_password?.message}
                            />
                          )}
                        />
                      </Col>
                    </Row>

                    <div className="d-grid gap-2">
                      {loading ? (
                        <Button variant="primary" className="rounded-pill py-2 shadow-sm" disabled>
                          <div className="d-flex justify-content-center">
                            <Circles type="Oval" color="#fff" width={20} height={20} />
                          </div>
                        </Button>
                      ) : (
                        <Button
                          type="submit"
                          disabled={!isValid}
                          variant="primary"
                          className="rounded-pill py-2 fw-bold shadow-sm"
                        >
                          Registrarse
                        </Button>
                      )}
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </Layout>
  );
};

export default connect(null, { signup })(Signup);
